import { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Container, Row, Col, Form, InputGroup } from "react-bootstrap";

import api from "../../services/api";
import Template from "../../template";
import { useAuth } from "../../context/auth";
import MiniaturaFoto from "../../components/FotoUsuario/Miniatura";

import * as S from "./styled";

/*
  Listagem do fluxo IA + Humano.

  Só aparecem redações de atividades vinculadas à turma com modo_correcao =
  'ia_humano'; a tela /redacoes exclui exatamente estas, para um corretor não
  finalizar por lá sem nunca ver o que a IA sugeriu.
*/

const ITENS_POR_PAGINA_PADRAO = 50;

/* Mesmo esquema das outras listagens: filtros na URL sobrevivem ao ir e voltar. */
function lerFiltrosDaUrl(search) {
  const q = new URLSearchParams(search);
  const pagina = parseInt(q.get("pagina"), 10);

  return {
    aluno: q.get("aluno") || "",
    turma: q.get("turma") || "",
    tema: q.get("tema") || "",
    pagina: Number.isFinite(pagina) && pagina > 0 ? pagina : 1,
  };
}

function montarQueryDeFiltros(f) {
  const q = new URLSearchParams();
  if (f.aluno) q.set("aluno", f.aluno);
  if (f.turma) q.set("turma", f.turma);
  if (f.tema) q.set("tema", f.tema);
  if (f.pagina > 1) q.set("pagina", String(f.pagina));
  const s = q.toString();
  return s ? `?${s}` : "";
}

/*
  Situação derivada no backend. "aguardando_revisao" é a que interessa ao corretor:
  a IA terminou e a redação está esperando alguém revisar e liberar.
*/
const SITUACOES = {
  envio_pendente_aluno: { texto: "Aguardando aluno", cor: "secondary" },
  sem_imagem: { texto: "Sem imagem para a IA", cor: "dark" },
  processando: { texto: "IA processando", cor: "info" },
  aguardando_ia: { texto: "Aguardando IA", cor: "info" },
  falhou: { texto: "IA falhou", cor: "danger" },
  aguardando_revisao: { texto: "Aguardando revisão", cor: "warning" },
  /* já tem corretor trabalhando nela; a coluna Corretor mostra quem */
  em_revisao: { texto: "Em revisão", cor: "info" },
  liberada: { texto: "Liberada ao aluno", cor: "success" },
};

const descreverSituacao = (s) => SITUACOES[s] || { texto: s || "—", cor: "secondary" };

function CorrecaoIAHumano() {
  const location = useLocation();
  const history = useHistory();
  const { user } = useAuth();

  const [filtrosIniciais] = useState(() => lerFiltrosDaUrl(location.search));

  const [loading, setLoading] = useState(true);
  const [redacoes, setRedacoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  const [buscaAluno, setBuscaAluno] = useState(filtrosIniciais.aluno);
  const [buscaTurma, setBuscaTurma] = useState(filtrosIniciais.turma);
  const [buscaTema, setBuscaTema] = useState(filtrosIniciais.tema);

  const [aluno, setAluno] = useState(filtrosIniciais.aluno);
  const [turma, setTurma] = useState(filtrosIniciais.turma);
  const [tema, setTema] = useState(filtrosIniciais.tema);

  const [paginaAtual, setPaginaAtual] = useState(filtrosIniciais.pagina);

  /*
    Mesma guarda das outras telas de correção: só redireciona quem sabidamente é
    estudante. Barrar "quem não é admin nem corretor" expulsaria o usuário no
    primeiro render, antes de o contexto carregar — o endpoint já devolve 403 de
    qualquer forma, então a checagem aqui é só de navegação.
  */
  if (user?.nivel === "e") {
    history.push("/classe");
  }

  /* Debounce nos campos de texto: evita uma requisição por tecla digitada. */
  useEffect(() => {
    const timer = setTimeout(() => {
      setAluno(buscaAluno);
      setTurma(buscaTurma);
      setTema(buscaTema);
      setPaginaAtual(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [buscaAluno, buscaTurma, buscaTema]);

  const queryDosFiltros = montarQueryDeFiltros({ aluno, turma, tema, pagina: paginaAtual });

  useEffect(() => {
    if (queryDosFiltros !== location.search) {
      history.replace(`${location.pathname}${queryDosFiltros}`);
    }
  }, [queryDosFiltros, location.pathname, location.search, history]);

  const [recarregar, setRecarregar] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function carregar(comSpinner) {
      if (comSpinner) setLoading(true);

      try {
        const params = { page: paginaAtual, limit: ITENS_POR_PAGINA_PADRAO };
        if (aluno) params.aluno = aluno;
        if (turma) params.turma = turma;
        if (tema) params.tema = tema;

        const { data } = await api.get("/correcao-ia-humano", {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setRedacoes(data.data || []);
        setTotal(data.paginacao?.totalCount || 0);
        setTotalPaginas(data.paginacao?.totalPages || 0);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Erro ao carregar redações IA + Humano:", err);
        }
      } finally {
        if (!controller.signal.aborted && comSpinner) setLoading(false);
      }
    }

    // sem spinner nas recargas automáticas: piscar a tabela a cada 15s incomoda
    carregar(recarregar === 0);
    return () => controller.abort();
  }, [aluno, turma, tema, paginaAtual, recarregar]);

  /*
    Enquanto houver alguma correção em andamento, recarrega sozinho. É a requisição
    da listagem que consulta a API da IA e faz o status avançar, então sem isto o
    corretor ficaria olhando "IA processando" até recarregar a página na mão.
  */
  const temProcessando = redacoes.some(
    (r) => r.situacao === "processando" || r.situacao === "aguardando_ia"
  );

  useEffect(() => {
    if (!temProcessando) return undefined;

    const timer = setInterval(() => setRecarregar((n) => n + 1), 15000);
    return () => clearInterval(timer);
  }, [temProcessando]);

  const limparFiltros = () => {
    setBuscaAluno("");
    setBuscaTurma("");
    setBuscaTema("");
    setPaginaAtual(1);
  };

  const destinoCorrecao = (idRedacao) => ({
    pathname: `/corrigir/redacao/${idRedacao}`,
    state: { voltarPara: `${location.pathname}${queryDosFiltros}` },
  });

  return (
    <Template>
      <S.MainWrapper>
        {/* `fluid` como nas demais telas de correção: sem ele o Container tem
            largura máxima fixa e a página fica estreita no meio do espaço livre */}
        <Container fluid>
          <Row>
            <Col sm={12}>
              <S.MainContent>
                {/* Cabeçalho */}
                <Row>
                  <Col sm={12}>
                    <div className="shadow-sm p-4 mb-4 bg-white rounded">
                      <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                          <i className="fas fa-robot text-primary mr-3" style={{ fontSize: "2rem" }}></i>
                          <div>
                            <h3 className="mb-0 text-dark font-weight-bold">Correção IA + Humano</h3>
                            <p className="mb-0 text-muted">
                              A IA corrige primeiro gerando comentarios e notas, o corretor realiza as marcações, revisa as notas e comentários e libera a correção para o aluno
                            </p>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="display-4 text-primary font-weight-bold">{total}</div>
                          <small className="text-muted font-weight-bold">
                            {total === 1 ? "redação encontrada" : "redações encontradas"}
                          </small>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Filtros */}
                <Row className="mb-4">
                  <Col sm={12}>
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light border-0">
                        <div className="d-flex align-items-center justify-content-between">
                          <h5 className="mb-0 text-dark font-weight-bold">
                            <i className="fas fa-filter text-primary mr-2"></i>
                            Filtros de Busca
                          </h5>
                          <button className="btn btn-outline-secondary btn-sm" onClick={limparFiltros}>
                            <i className="fas fa-times mr-1"></i>
                            Limpar Filtros
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <Row>
                          <Col xs={12} sm={6} md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label className="font-weight-bold text-dark">
                                <i className="fas fa-user text-primary mr-1"></i>
                                Aluno
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Prepend>
                                  <InputGroup.Text>
                                    <i className="fas fa-search"></i>
                                  </InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                  type="text"
                                  placeholder="Buscar aluno..."
                                  value={buscaAluno}
                                  onChange={(e) => setBuscaAluno(e.target.value)}
                                />
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          <Col xs={12} sm={6} md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label className="font-weight-bold text-dark">
                                <i className="fas fa-users text-primary mr-1"></i>
                                Turma
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Prepend>
                                  <InputGroup.Text>
                                    <i className="fas fa-search"></i>
                                  </InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                  type="text"
                                  placeholder="Buscar turma..."
                                  value={buscaTurma}
                                  onChange={(e) => setBuscaTurma(e.target.value)}
                                />
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          <Col xs={12} sm={6} md={4} className="mb-3">
                            <Form.Group>
                              <Form.Label className="font-weight-bold text-dark">
                                <i className="fas fa-book text-primary mr-1"></i>
                                Tema
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Prepend>
                                  <InputGroup.Text>
                                    <i className="fas fa-search"></i>
                                  </InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                  type="text"
                                  placeholder="Buscar tema..."
                                  value={buscaTema}
                                  onChange={(e) => setBuscaTema(e.target.value)}
                                />
                              </InputGroup>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Tabela */}
                <Row>
                  <Col sm={12}>
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light border-0">
                        <h5 className="mb-0 text-dark font-weight-bold">
                          <i className="fas fa-table text-primary mr-2"></i>
                          Lista de Redações
                        </h5>
                      </div>
                      <div className="card-body p-0">
                        {loading ? (
                          <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="sr-only">Carregando...</span>
                            </div>
                            <p className="mt-3 text-muted">Carregando redações...</p>
                          </div>
                        ) : redacoes.length === 0 ? (
                          <div className="text-center p-5">
                            <i className="fas fa-robot text-muted" style={{ fontSize: "3rem" }}></i>
                            <h5 className="mt-3 text-muted">Nenhuma redação neste fluxo</h5>
                            <p className="text-muted">
                              Marque uma atividade como <strong>IA + Humano</strong> ao adicioná-la a uma turma
                              para que as redações apareçam aqui.
                            </p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover mb-0">
                              <thead className="thead-light">
                                <tr>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "170px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-user mr-1"></i> Aluno
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "160px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-book mr-1"></i> Tema
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "100px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-users mr-1"></i> Turma
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "95px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-calendar mr-1"></i> Enviada
                                  </th>
                                  {/* data da liberação ao aluno, como nas outras telas */}
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "95px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-calendar-check mr-1"></i> Corrigida
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "115px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-robot mr-1"></i> Nota da IA
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "125px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-user-check mr-1"></i> Nota do corretor
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "130px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-user-edit mr-1"></i> Corretor
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark" style={{ minWidth: "125px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-info-circle mr-1"></i> Situação
                                  </th>
                                  <th className="border-0 font-weight-bold text-dark text-center" style={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                    <i className="fas fa-cogs mr-1"></i> Ação
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {redacoes.map((r) => {
                                  const situacao = descreverSituacao(r.situacao);
                                  const podeAbrir = !!r.pdf;

                                  return (
                                    <tr key={r.id_redacao}>
                                      <td className="align-middle">
                                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                                          <MiniaturaFoto foto={r.aluno_foto} nome={r.aluno} />
                                          <span>{r.aluno}</span>
                                        </div>
                                      </td>
                                      <td className="align-middle">{r.tema || "—"}</td>
                                      <td className="align-middle">{r.turma || "Sem turma"}</td>
                                      <td className="align-middle text-muted">
                                        {r.data ? new Date(r.data).toLocaleDateString("pt-BR") : "—"}
                                      </td>
                                      <td className="align-middle text-muted">
                                        {/* vem como "AAAA-MM-DD"; passar por Date mostraria o dia anterior */}
                                        {r.data_correcao
                                          ? r.data_correcao.split("-").reverse().join("/")
                                          : "—"}
                                      </td>
                                      <td className="align-middle">
                                        {r.nota_ia === null || r.nota_ia === undefined ? (
                                          <span className="text-muted">—</span>
                                        ) : (
                                          <span className="text-muted">{r.nota_ia}/1000</span>
                                        )}
                                      </td>
                                      <td className="align-middle">
                                        {/*
                                          A nota que vale é esta: a da IA é sugestão e o
                                          corretor pode ter mudado. Só existe depois que ele
                                          finaliza; antes fica "—", não 0.
                                        */}
                                        {r.nota_final_corretor === null || r.nota_final_corretor === undefined ? (
                                          <span className="text-muted">—</span>
                                        ) : (
                                          <>
                                            <strong className="text-dark">{r.nota_final_corretor}/1000</strong>
                                            {r.nota_ia !== null &&
                                              r.nota_ia !== undefined &&
                                              Number(r.nota_ia) !== Number(r.nota_final_corretor) && (
                                                <div
                                                  className="text-muted"
                                                  style={{ fontSize: "11px" }}
                                                  title="O corretor alterou a nota sugerida pela IA"
                                                >
                                                  ajustada pelo corretor
                                                </div>
                                              )}
                                          </>
                                        )}
                                      </td>
                                      <td className="align-middle">
                                        {/* só existe depois que alguém finaliza a revisão */}
                                        {r.corretor ? (
                                          <span className="text-dark">{r.corretor}</span>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td className="align-middle">
                                        <span
                                          className={`badge badge-${situacao.cor}`}
                                          style={{ borderRadius: "12px", whiteSpace: "normal" }}
                                          title={r.erro_ia || undefined}
                                        >
                                          {situacao.texto}
                                        </span>
                                      </td>
                                      <td className="align-middle text-center">
                                        {podeAbrir ? (
                                          <Link
                                            to={destinoCorrecao(r.id_redacao)}
                                            className={`btn btn-sm ${
                                              r.situacao === "liberada" ? "btn-warning" : "btn-danger"
                                            }`}
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            <i className="fas fa-edit mr-1"></i>
                                            {r.situacao === "liberada" ? "Editar Correção" : "Revisar"}
                                          </Link>
                                        ) : (
                                          <span className="text-muted">Aguardando aluno</span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Paginação */}
                {totalPaginas > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded flex-wrap">
                    <span className="text-muted">
                      <strong>Página {paginaAtual} de {totalPaginas}</strong>
                    </span>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-outline-primary btn-sm mr-2"
                        onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                        disabled={paginaAtual === 1}
                      >
                        <i className="fas fa-angle-left mr-1"></i>
                        Anterior
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                        disabled={paginaAtual === totalPaginas}
                      >
                        Próxima
                        <i className="fas fa-angle-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                )}
              </S.MainContent>
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>
    </Template>
  );
}

export default CorrecaoIAHumano;
